import type { TMDbPerson } from '../services/tmdbApi';

const STORAGE_KEY = 'watchlists_person_nav_preview_v1';
const MAX_AGE_MS = 120_000;

let strictModeReplayPreview: PersonDetailNavPreviewStash | null = null;

export interface PersonDetailNavPreviewStash {
  personId: string;
  name: string;
  profileUrl: string | null;
  profilePath: string | null;
  knownForDepartment: string | null;
  tmdbNumericId: number | null;
  stashedAt: number;
}

function personIdsMatchForNav(stashedId: string, routePersonId: string): boolean {
  if (stashedId === routePersonId) return true;
  try {
    if (decodeURIComponent(stashedId) === decodeURIComponent(routePersonId)) return true;
  } catch {
    /* ignore */
  }
  const s = stashedId.trim();
  const r = routePersonId.trim();
  if (/^\d+$/.test(s) && /^\d+$/.test(r)) return s === r;
  return false;
}

/**
 * Call immediately before navigating to `/person/:id` so People detail can paint name/photo without waiting for API.
 */
export function stashPersonDetailNavPreviewFromPerson(
  navigationPersonId: string,
  person: {
    name: string;
    profile_path?: string | null;
    known_for_department?: string | null;
    id?: number | string;
  },
  profileUrlResolver: () => string | null
): void {
  if (!navigationPersonId || navigationPersonId === 'unknown') return;
  let tmdbNumericId: number | null = null;
  if (typeof person.id === 'number' && Number.isFinite(person.id)) {
    tmdbNumericId = person.id;
  } else if (typeof person.id === 'string' && /^\d+$/.test(person.id.trim())) {
    tmdbNumericId = Number(person.id.trim());
  } else if (/^\d+$/.test(navigationPersonId.trim())) {
    tmdbNumericId = Number(navigationPersonId.trim());
  }
  const profilePath =
    person.profile_path && person.profile_path !== 'N/A' ? String(person.profile_path) : null;
  const payload: PersonDetailNavPreviewStash = {
    personId: navigationPersonId,
    name: person.name || '',
    profileUrl: profileUrlResolver(),
    profilePath,
    knownForDepartment: person.known_for_department ?? null,
    tmdbNumericId,
    stashedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / quota */
  }
}

export function takePersonDetailNavPreview(expectedRoutePersonId: string): PersonDetailNavPreviewStash | null {
  if (!expectedRoutePersonId) return null;

  if (strictModeReplayPreview) {
    if (!personIdsMatchForNav(strictModeReplayPreview.personId, expectedRoutePersonId)) {
      strictModeReplayPreview = null;
    } else {
      const replay = strictModeReplayPreview;
      strictModeReplayPreview = null;
      if (Date.now() - (replay.stashedAt || 0) > MAX_AGE_MS) return null;
      return replay;
    }
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    const data = JSON.parse(raw) as PersonDetailNavPreviewStash;
    if (!data?.personId) return null;
    if (Date.now() - (data.stashedAt || 0) > MAX_AGE_MS) return null;
    if (!personIdsMatchForNav(data.personId, expectedRoutePersonId)) return null;
    strictModeReplayPreview = data;
    return data;
  } catch {
    return null;
  }
}

export function clearPersonDetailNavPreviewStrictReplay(): void {
  strictModeReplayPreview = null;
}

/** Minimal `TMDbPerson` for instant paint; full fetch replaces this. */
export function tmdbPersonFromNavPreview(routePersonId: string, p: PersonDetailNavPreviewStash): TMDbPerson {
  const idFromRoute = /^\d+$/.test(routePersonId.trim()) ? parseInt(routePersonId.trim(), 10) : 0;
  const id = p.tmdbNumericId ?? idFromRoute;
  const profile_path =
    p.profilePath && !p.profilePath.startsWith('http')
      ? p.profilePath
      : p.profileUrl && p.profileUrl.startsWith('http')
        ? p.profileUrl
        : null;

  return {
    id,
    name: p.name || '…',
    biography: '',
    birthday: null,
    deathday: null,
    place_of_birth: null,
    profile_path,
    adult: false,
    imdb_id: null,
    homepage: null,
    known_for_department: p.knownForDepartment || 'Acting',
    popularity: 0,
    gender: 0,
    also_known_as: [],
  };
}

export function backdropUrlFromPersonPreviewProfile(profileUrl: string | null): string | null {
  if (!profileUrl) return null;
  if (profileUrl.includes('image.tmdb.org')) {
    return profileUrl
      .replace('/t/p/w45/', '/t/p/w780/')
      .replace('/t/p/w185/', '/t/p/w780/')
      .replace('/t/p/w200/', '/t/p/w780/')
      .replace('/t/p/w500/', '/t/p/w780/');
  }
  return profileUrl;
}
